import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, Check } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';
import PageTransition from '@/components/PageTransition';
import { saveSession } from '@/components/ProtectedRoute';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, formData, {
        withCredentials: true,
        timeout: 30000 // 30 secondes timeout
      });
      
      // Sauvegarder la session en localStorage (backup pour PWA iOS)
      saveSession(response.data.session_token, response.data.user);
      
      toast.success(t('auth.loginSuccess'));
      navigate('/dashboard', { state: { user: response.data.user } });
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Le serveur met trop de temps à répondre. Réessayez.');
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        toast.error('Problème de connexion réseau. Vérifiez votre connexion internet.');
      } else if (error.response?.status === 401) {
        toast.error('Email ou mot de passe incorrect');
      } else {
        const detail = error.response?.data?.detail;
        const errorMsg = Array.isArray(detail) ? detail[0]?.msg || 'Erreur de connexion' : detail || 'Erreur de connexion';
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <PageTransition>
      <div className={`min-h-screen flex relative z-10 ${isDark ? '' : 'bg-white'}`}>
        {/* Form Side */}
        <div className="flex-1 flex items-center justify-center p-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="w-full max-w-md"
          >
            <div className="flex justify-end mb-6">
              <ThemeToggle />
            </div>
            
            <div className="text-center mb-10">
              <Link to="/" className="inline-flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center shadow-lg shadow-violet-500/25">
                  <Sparkle size={22} weight="fill" className="text-white" />
                </div>
                <span className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>SIMBLYO</span>
              </Link>
              <h1 className={`text-3xl md:text-4xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Content de vous revoir
              </h1>
              <p className={`text-base ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                Connectez-vous pour accéder à votre assistant marketing
              </p>
            </div>

            <div className={`p-8 space-y-6 rounded-2xl ${
              isDark 
                ? 'glass-card' 
                : 'bg-white border border-gray-200 shadow-lg'
            }`}>
              <Button
                type="button"
                variant="outline"
                className={`w-full transition-all ${
                  isDark 
                    ? 'bg-white/[0.03] border-white/[0.1] hover:bg-white/[0.06]' 
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.googleLogin')}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className={`w-full border-t ${isDark ? 'border-white/[0.08]' : 'border-gray-200'}`} />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className={`px-3 ${isDark ? 'bg-[hsl(230,22%,16%)] text-white/40' : 'bg-white text-gray-500'}`}>
                    {t('common.or')}
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className={isDark ? 'text-white/70' : 'text-gray-700'}>{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className={isDark ? 'text-white/70' : 'text-gray-700'}>{t('auth.password')}</Label>
                  <PasswordInput
                    id="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="login-password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-premium py-6" 
                  disabled={loading} 
                  data-testid="login-submit-btn"
                >
                  {loading ? t('auth.connecting') : t('auth.login')}
                </Button>
                
                <div className="text-center">
                  <Link 
                    to="/forgot-password" 
                    className={`text-sm hover:underline ${isDark ? 'text-white/50 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'}`}
                    data-testid="forgot-password-link"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
              </form>

              <p className={`text-center text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                {t('auth.noAccount')}{' '}
                <Link to="/register" className="text-violet-500 hover:text-violet-400 transition-colors font-medium">
                  {t('auth.register')}
                </Link>
              </p>
            </div>
          </motion.div>
        </div>

        {/* Decorative Side */}
        <div className={`hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden ${
          isDark ? '' : 'bg-gray-50'
        }`}>
          <div className="absolute inset-0 bg-gradient-to-br from-violet-600/20 via-pink-600/10 to-transparent"></div>
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-violet-500/20 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-1/4 left-1/4 w-48 h-48 bg-pink-500/20 rounded-full filter blur-3xl"></div>
          
          <div className="max-w-md text-center relative z-10">
            <h2 className={`text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('auth.welcomeSimblyo')}
            </h2>
            <p className={`text-lg mb-8 ${isDark ? 'text-white/50' : 'text-gray-600'}`}>
              {t('auth.simblyoDesc')}
            </p>
            <div className="space-y-4 text-left">
              {[t('auth.features.aiContent'), t('auth.features.seoPages'), t('auth.features.googleTracking')].map((feature, i) => (
                <div key={i} className={`flex items-center gap-3 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    isDark ? 'bg-violet-500/20' : 'bg-violet-100'
                  }`}>
                    <Check size={14} weight="bold" className="text-violet-500" />
                  </div>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}
